import { Check, ExternalLink, MapPin, Pencil, RotateCcw, Trash2 } from "lucide-react";

import { AjustesDeHomologacion } from "@/components/referencias/AjustesDeHomologacion";
import { DescripcionDelAviso } from "@/components/referencias/DescripcionDelAviso";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  describirPrecision,
  distancia,
  fechaCorta,
  hectareas,
  numero,
  pesos,
} from "@/lib/formatoDeMercado";
import type { AjusteDeHomologacion } from "@/lib/homologacion";
import { describirClaseDeSuelo, type ReferenciaDelInforme } from "@/lib/referencias";
import { cn } from "@/lib/utils";

interface FichaDeReferenciaProps {
  referencia: ReferenciaDelInforme;
  onValidar?: (validada: boolean) => void;
  onDescartar?: () => void;
  onRestaurar?: () => void;
  ocupada?: boolean;
  /**
   * El catálogo de homologación. LLEGA SOLO DESDE LA FICHA DEL INFORME: el
   * módulo de referencias ELIGE los comparables y el capítulo de comparación
   * los GESTIONA, así que sin estas dos props la ficha no ofrece homologar.
   */
  escalaDeHomologacion?: AjusteDeHomologacion[];
  onAjustar?: (categoriaIds: number[]) => void;
  /**
   * Abrir el modal que corrige los DATOS del comparable.
   *
   * Va por prop, igual que `onAjustar`, y por el mismo motivo: la frontera es
   * una sola condición y no una regla escrita en dos lugares. Sin ella la ficha
   * no ofrece editar — que es lo que pasa en la pantalla de enlazadas del
   * módulo de referencias.
   *
   * NO se ofrece sobre una ficha descartada: está en la papelera, así que
   * corregirla sería trabajar sobre algo que se sacó del análisis.
   */
  onEditar?: () => void;
}

/**
 * Una referencia que YA ES DEL INFORME: la copia congelada más su estado.
 *
 * SE PARECE A `FichaDeComparable` Y NO ES LA MISMA, a propósito. Aquella
 * describe una ficha VIVA del catálogo y se elige; esta describe lo que el
 * informe guardó y se decide sobre ella. Tres diferencias que se ven:
 *
 *   * NO tiene casilla ni barra contra la mediana. La barra comparaba contra
 *     el filtro del catálogo, y acá no hay filtro: el conjunto ES el informe.
 *   * SÍ tiene los botones de validar y descartar.
 *   * Sus cifras llegan como STRING (`decimal:*` de PHP) y no como número. Es
 *     el precio de haberlas congelado: lo que se guarda para un documento que
 *     se firma no pasa por un float.
 *
 * Fundirlas obligaría a la del catálogo a cargar campos que no tiene y a esta a
 * fingir que sigue conectada a la otra base.
 */
export function FichaDeReferencia({
  referencia,
  onValidar,
  onDescartar,
  onRestaurar,
  ocupada = false,
  escalaDeHomologacion,
  onAjustar,
  onEditar,
}: FichaDeReferenciaProps) {
  const superficieM2 = aNumero(referencia.superficie_terreno_m2);
  const precio = aNumero(referencia.precio_monto);

  const valorUnitarioPesos =
    referencia.precio_moneda === "CLP" && precio !== null && superficieM2
      ? precio / (superficieM2 / 10_000)
      : null;

  const estimada =
    referencia.precision_ubicacion !== "exacta" &&
    referencia.precision_ubicacion !== "aproximada";

  const claseDeSuelo = describirClaseDeSuelo(referencia.clase_suelo_detalle);

  return (
    <article
      className={cn(
        "rounded-xl border-[1.5px] p-4",
        referencia.descartada
          ? "border-border bg-muted/30"
          : referencia.validada
            ? "border-[color-mix(in_oklab,var(--color-primary)_45%,transparent)]"
            : "border-border",
      )}
    >
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <Badge variant={referencia.clase === "transaccion" ? "info" : "warning"}>
          {referencia.clase === "transaccion" ? "TRANSACCIÓN CBR" : "OFERTA"}
        </Badge>

        {referencia.referencia && (
          <span className="text-ink-muted font-mono text-xs">
            {referencia.clase === "transaccion"
              ? `Rol ${referencia.referencia}`
              : referencia.referencia}
          </span>
        )}

        {referencia.distancia_m !== null && (
          <span className="text-ink-muted inline-flex items-center gap-1 text-xs">
            <MapPin className="size-3.5" />
            {distancia(referencia.distancia_m)}
          </span>
        )}

        <div className="ml-auto flex items-center gap-1.5">
          {referencia.descartada ? (
            <Button variant="outline" size="sm" onClick={onRestaurar} disabled={ocupada}>
              <RotateCcw className="size-3.5" />
              Restaurar
            </Button>
          ) : (
            <>
              {onEditar && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onEditar}
                  disabled={ocupada}
                  // El nombre va en la etiqueta accesible y no en el botón: en
                  // una lista de comparables hay un "Editar" por tarjeta, y sin
                  // el nombre un lector de pantalla los enumera todos iguales.
                  aria-label={`Editar ${referencia.titulo}`}
                >
                  <Pencil className="size-3.5" />
                  Editar
                </Button>
              )}

              <Button
                variant={referencia.validada ? "soft" : "outline"}
                size="sm"
                onClick={() => onValidar?.(!referencia.validada)}
                disabled={ocupada}
                // El botón dice el ESTADO, no la acción, porque es un
                // interruptor: "Validada" con el check puesto se lee como lo
                // que la ficha es, y volver a apretarlo lo deshace.
                title={
                  referencia.validada
                    ? "Quitar la marca de dato validado"
                    : "Marcar como dato validado"
                }
              >
                <Check className="size-3.5" />
                {referencia.validada ? "Validada" : "Sin validar"}
              </Button>

              <Button
                variant="ghost"
                size="sm"
                onClick={onDescartar}
                disabled={ocupada}
                aria-label={`Descartar ${referencia.titulo}`}
              >
                <Trash2 className="size-3.5" />
              </Button>
            </>
          )}
        </div>
      </div>

      <h3 className="text-ink text-sm font-bold">{referencia.titulo}</h3>

      <div className="text-ink-muted mt-1.5 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs">
        <span>{referencia.comuna || "Comuna sin informar"}</span>
        {/* VA ACÁ Y NO EN EL `<dl>` DE MÉTRICAS: aquel bloque es
            `font-mono tabular-nums`, pensado para cifras que se comparan en
            columna, y un código como "IIIr" ahí queda desalineado y sugiere
            que es una magnitud. La clase de suelo es un atributo del
            TERRITORIO, igual que la comuna con la que comparte fila. */}
        {claseDeSuelo && <span>{claseDeSuelo}</span>}
        <span className="font-mono">{fechaCorta(referencia.fecha)}</span>
        <span>{referencia.fuente}</span>
        <span
          className={cn(
            "inline-flex items-center gap-1.5",
            estimada && "text-warning-soft-foreground",
          )}
        >
          <span
            className={cn(
              "size-1.5 rounded-full",
              estimada ? "bg-warning-dot" : "bg-success-dot",
            )}
          />
          {describirPrecision(referencia.precision_ubicacion)}
        </span>
      </div>

      <dl className="mt-3 grid grid-cols-[repeat(auto-fit,minmax(7.5rem,1fr))] gap-3">
        <Metrica rotulo="Superficie ha" valor={hectareas(superficieM2)} />
        {/* EL RÓTULO SIGUE A LA MONEDA, y no es prolijidad de formato.
            Medido sobre una ficha real (10-09-2026): "ROMERILLO 2 LOTE B", 80
            ha, `precio_moneda = 'UF'` y 1.639,29 de monto. Rotulado "$" salía
            "$ 1.639" — mil seiscientos pesos por ochenta hectáreas, una cifra
            falsa hacia abajo por un factor de ~39.000, en la pantalla desde la
            que se homologa un comparable para un informe pericial. El catálogo
            guarda las dos monedas a propósito; la tarjeta tiene que decir cuál
            está mostrando. */}
        {referencia.precio_moneda === "UF" ? (
          <Metrica rotulo="Valor total UF" valor={numero(precio, 2)} />
        ) : (
          <Metrica rotulo="Valor total $" valor={pesos(precio)} />
        )}
        {/* El unitario en pesos queda en "—" cuando el precio no viene en
            pesos, y así se queda: convertirlo exigiría una tasa, y una cifra
            que no se puede reconstruir es peor que su ausencia acá. */}
        <Metrica rotulo="Valor unit. $/ha" valor={pesos(valorUnitarioPesos)} />
        <Metrica rotulo="UF/ha" valor={numero(ufHa(referencia), 0)} />
      </dl>

      {/* La prosa del aviso, CONGELADA el día que se enlazó. Va después de las
          cifras y antes de la traza: primero cuánto y cuánto mide, después qué
          decía el aviso, al final quién lo dio por bueno. */}
      <DescripcionDelAviso descripcion={referencia.descripcion} className="mt-3" />

      {/* Solo donde llega `onAjustar`, que hoy es la ficha del informe. NO se
          filtra además por `descartada`: la papelera se pide por su propio
          endpoint y ninguna de sus fichas llega hasta acá con la prop puesta,
          así que sería un cerrojo sin puerta que guardar. */}
      {onAjustar && (
        <AjustesDeHomologacion
          escala={escalaDeHomologacion ?? []}
          elegidos={referencia.ajustes ?? []}
          clase={referencia.clase}
          ufHa={ufHa(referencia)}
          onCambiar={onAjustar}
          deshabilitado={ocupada}
        />
      )}

      {/* La trazabilidad de la decisión. En un informe pericial "quién dio
          esto por bueno y cuándo" es una pregunta que se hace. */}
      {(referencia.validada || referencia.descartada) && (
        <p className="text-ink-hint mt-2.5 text-xs">
          {referencia.descartada
            ? `Descartada el ${fechaCorta(referencia.descartado_en)}`
            : `Validada el ${fechaCorta(referencia.validado_en)}`}
          {referencia.validado_por && !referencia.descartada && (
            <> por {referencia.validado_por.first_name} {referencia.validado_por.last_name}</>
          )}
        </p>
      )}

      {referencia.url && (
        <a
          href={referencia.url}
          target="_blank"
          rel="noreferrer noopener"
          className="text-primary mt-2 inline-flex max-w-full items-center gap-1 truncate text-xs hover:underline"
        >
          <ExternalLink className="size-3 shrink-0" />
          <span className="truncate">{referencia.url}</span>
        </a>
      )}
    </article>
  );
}

function Metrica({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div>
      <dt className="text-ink-hint text-[0.6875rem] font-extrabold tracking-[0.08em] uppercase">
        {rotulo}
      </dt>
      <dd className="text-ink mt-0.5 font-mono text-sm tabular-nums">{valor}</dd>
    </div>
  );
}

/**
 * Los `decimal:*` de PHP llegan como STRING, y `null` tiene que seguir siendo
 * `null`: un `Number(null)` da 0, y un cero acá diría que la ficha vale cero
 * cuando lo que pasa es que el dato no está.
 */
function aNumero(valor: string | null): number | null {
  if (valor === null) return null;
  const numero = Number(valor);

  return Number.isFinite(numero) ? numero : null;
}

/**
 * UF por hectárea, derivado al leer.
 *
 * NO se congeló en la fila —a diferencia del precio y la superficie— porque es
 * una división entre dos columnas que sí están: guardarlo sería el mismo dato
 * en dos formatos, y el día que no coincidieran habría que decidir cuál manda.
 */
function ufHa(referencia: ReferenciaDelInforme): number | null {
  const uf = aNumero(referencia.precio_uf);
  const m2 = aNumero(referencia.superficie_terreno_m2);

  if (uf === null || m2 === null || m2 <= 0) return null;

  return uf / (m2 / 10_000);
}
